// One function per review route. Re-edits pass no loadingKey on purpose: they
// are fire-and-move-on, and the page tracks them per photo instead.
const BASE = 'photo-pipeline/review';

export const getQueue = fetchResource => fetchResource({ method: 'GET', resource: `${BASE}/pending`, loadingKey: 'queue' });
// `attempts` is the version of the edit the reviewer was shown: the server
// refuses the approve (409, with the current `attempts`) if it has moved on.
// Quiet: PieceReview shows its failures itself, on the shot or above the piece.
export const approve = (fetchResource, photoId, attempts) =>
    fetchResource({ method: 'POST', resource: `${BASE}/${photoId}/approve`, body: { attempts }, quiet: true });
// `referencePhotoId`: another shot of the piece Gemini sees too (the Detail
// reference), sent only when one is chosen.
const withReference = (body, referencePhotoId) => (referencePhotoId ? { ...body, referencePhotoId } : body)
export const reroll = (fetchResource, photoId, referencePhotoId = null) =>
    fetchResource({ method: 'POST', resource: `${BASE}/${photoId}/regenerate`, body: withReference({ mode: 'blind' }, referencePhotoId) });
export const getAttempts = (fetchResource, photoId) => fetchResource({ method: 'GET', resource: `${BASE}/${photoId}/attempts` });
export const reeditWithPrompt = (fetchResource, photoId, prompt, referencePhotoId = null) =>
    fetchResource({ method: 'POST', resource: `${BASE}/${photoId}/regenerate-with-prompt`, body: withReference({ prompt }, referencePhotoId) });
// The shadow-only re-roll: Photoroom again on this version's Gemini image.
export const getShadowOptions = (fetchResource, photoId) => fetchResource({ method: 'GET', resource: `${BASE}/${photoId}/shadow` });
export const reshadow = (fetchResource, photoId, settings) =>
    fetchResource({ method: 'POST', resource: `${BASE}/${photoId}/reshadow`, body: settings });
// A failed first edit goes back to the batch rather than to a paid interactive edit.
export const retryInBatch = (fetchResource, photoId) => fetchResource({ method: 'POST', resource: `${BASE}/${photoId}/retry` });
export const processNow = fetchResource => fetchResource({ method: 'POST', resource: `${BASE}/process-now`, loadingKey: 'processNow' });
export const reject = (fetchResource, photoId, { reason, note }) =>
    fetchResource({ method: 'POST', resource: `${BASE}/${photoId}/reject`, body: { reason, note } });
// Removes the shot from its piece for good; the piece goes ahead without it.
// Quiet: PieceReview shows a failure itself, above the piece.
export const removeShot = (fetchResource, photoId) =>
    fetchResource({ method: 'POST', resource: `${BASE}/${photoId}/remove`, quiet: true });
// "Use this version": an earlier edit of the shot becomes the current one.
// `attempts` is the version on screen, the same race guard approve sends.
// Quiet: VersionHistory shows a refusal itself.
export const restoreVersion = (fetchResource, photoId, attemptNo, attempts) =>
    fetchResource({ method: 'POST', resource: `${BASE}/${photoId}/restore`, body: { attemptNo, attempts }, quiet: true });
// Takes back a shot's approval (refused while its piece is publishing or
// live). Quiet: PieceReview and the piece preview show the failure themselves.
export const unapprove = (fetchResource, photoId) =>
    fetchResource({ method: 'POST', resource: `${BASE}/${photoId}/unapprove`, quiet: true });
// Undoes a rejection: the shot goes back to review (or to the edit queue,
// if it was rejected before it had an edit). `attempts` is the version on
// screen, the same race guard approve sends. Quiet: PieceReview words a
// refusal itself.
export const unreject = (fetchResource, photoId, attempts) =>
    fetchResource({ method: 'POST', resource: `${BASE}/${photoId}/unreject`, body: { attempts }, quiet: true });
// Fix color's brush: the automatic zones and any masks already painted on the
// version, then a dry run, then Apply (a new version, which still needs
// Approve). The first two are quiet: the brush shows their failures itself.
export const getColorZones = (fetchResource, photoId) =>
    fetchResource({ method: 'GET', resource: `${BASE}/${photoId}/color-zones`, quiet: true });
export const previewColor = (fetchResource, photoId, masks) =>
    fetchResource({ method: 'POST', resource: `${BASE}/${photoId}/color-match/preview`, body: { masks }, quiet: true });
export const colorMatch = (fetchResource, photoId, masks) =>
    fetchResource({ method: 'POST', resource: `${BASE}/${photoId}/color-match`, body: { masks } });
// Replace: his hand-edited PNG becomes a new version ('manual', "Your edit"),
// which still needs Approve. `shadow` is 'add' (Photoroom's shadow, re-framed
// to a 2048 px square) or 'none' (his file as is). Quiet: the Replace dialog
// words each refusal by its status.
export function replaceShot(fetchResource, photoId, file, shadow) {
    const body = new FormData();
    body.append('file', file);
    body.append('shadow', shadow);
    return fetchResource({ method: 'POST', resource: `${BASE}/${photoId}/replace`, body, quiet: true });
}
// Mirror: the version on screen, flipped left to right, becomes a new version
// ("Mirrored"), which still needs Approve. `attempts` is that version, the
// same race guard approve sends. Quiet: PieceReview words a refusal itself.
export const mirrorShot = (fetchResource, photoId, attempts) =>
    fetchResource({ method: 'POST', resource: `${BASE}/${photoId}/mirror`, body: { attempts }, quiet: true });
